/**
 * useActiveMrciCycle — ciclo MRCI ativo do jogo selecionado.
 *
 * Ciclos MRCI duram 8 semanas (56 dias). O hook lê direto do Supabase
 * (RLS "leitura pública" em mrci_cycles) e devolve o progresso em semanas
 * + o prefixo da season ativa (ex.: "S06") para uso no TopBar/Console.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { TTL } from '../lib/queryClient';

export const ACTIVE_MRCI_CYCLE_KEY = (gameId?: string) =>
  ['mrci-cycle', 'active', gameId] as const;

export interface ActiveMrciCycle {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  /** Semana atual (1-based), já clampada entre 1 e totalWeeks */
  currentWeek: number;
  /** Total de semanas do ciclo (8 para ciclos padrão) */
  totalWeeks: number;
  /** Percentual de semanas decorridas (0-100) */
  progressPct: number;
  /** Prefixo da season ativa — ex.: "S06" */
  seasonPrefix: string;
}

/** Converte 'YYYY-MM-DD' (date-only, UTC) em ms UTC — evita bug de timezone. */
function dateOnlyMs(value: string): number {
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return NaN;
  return Date.UTC(y, m - 1, d);
}

/**
 * Calcula o progresso semanal de um ciclo.
 * Semana 1 = dias 0..6 a partir do start_date.
 */
export function computeCycleProgress(
  startDate: string,
  endDate: string,
  now: Date = new Date()
): Pick<ActiveMrciCycle, 'currentWeek' | 'totalWeeks' | 'progressPct'> {
  const startMs = dateOnlyMs(startDate);
  const endMs = dateOnlyMs(endDate);

  // Total de semanas arredondado (56 dias → 8 semanas)
  const totalDays = Math.round((endMs - startMs) / 86400000) + 1;
  const totalWeeks = Math.max(1, Math.round(totalDays / 7));

  // "Hoje" em UTC (mesma base dos campos date-only do banco)
  const todayMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const daysSinceStart = Math.floor((todayMs - startMs) / 86400000);

  const currentWeek = Math.min(totalWeeks, Math.max(1, Math.floor(daysSinceStart / 7) + 1));
  const progressPct = Math.round((currentWeek / totalWeeks) * 100);

  return { currentWeek, totalWeeks, progressPct };
}

/** "Season 6" → "S06"; fallback: label em caixa alta. */
export function seasonPrefixFromLabel(label: string): string {
  const match = String(label || '').match(/(\d+)/);
  if (match) return `S${match[1].padStart(2, '0')}`;
  return String(label || '').trim().toUpperCase();
}

function buildCycle(row: any): ActiveMrciCycle {
  const { currentWeek, totalWeeks, progressPct } = computeCycleProgress(
    row.start_date,
    row.end_date
  );
  return {
    id: row.id,
    label: row.label,
    startDate: row.start_date,
    endDate: row.end_date,
    currentWeek,
    totalWeeks,
    progressPct,
    seasonPrefix: seasonPrefixFromLabel(row.label),
  };
}

/**
 * Ciclo MRCI ativo de um jogo.
 * Retorna `null` quando o jogo não tem ciclo ativo (não renderiza nada).
 */
export function useActiveMrciCycle(gameId?: string) {
  const query = useQuery<ActiveMrciCycle | null>({
    queryKey: ACTIVE_MRCI_CYCLE_KEY(gameId),
    enabled: !!gameId,
    staleTime: TTL.MODERATE,
    queryFn: async () => {
      const { data, error } = await supabase
        // tipagem de mrci_cycles ainda não foi gerada em database.types.ts
        .from('mrci_cycles' as any)
        .select('id, label, start_date, end_date, is_active, processed_at')
        .eq('game_id', gameId as string)
        .eq('is_active', true)
        .is('processed_at', null)
        .order('start_date', { ascending: false })
        .limit(1);

      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : null;
      if (!row || !row.start_date || !row.end_date) return null;
      return buildCycle(row);
    },
  });

  return {
    cycle: query.data ?? null,
    isLoading: query.isLoading,
  };
}
